import { Checkbox, Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import type { EventTypeMeta } from './types';

export interface EventSubscriptionCheckboxGridProps {
  availableEvents: EventTypeMeta[];
  selectedEventTypes: string[];
  onChange: (types: string[]) => void;
  disabled?: boolean;
}

/**
 * Checkbox list for binding an integration to a form with an event-type
 * subscription mask.
 *
 * The parent owns `selectedEventTypes` so this component stays controlled —
 * makes it trivial to reset state when the user cancels an edit.
 */
export function EventSubscriptionCheckboxGrid({
  availableEvents,
  selectedEventTypes,
  onChange,
  disabled,
}: EventSubscriptionCheckboxGridProps) {
  const { t } = useTranslation();

  const toggle = (type: string, checked: boolean) => {
    if (checked) {
      if (!selectedEventTypes.includes(type)) {
        onChange([...selectedEventTypes, type]);
      }
    } else {
      onChange(selectedEventTypes.filter((t) => t !== type));
    }
  };

  if (availableEvents.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        {t('integrations.events.empty')}
      </Text>
    );
  }

  return (
    <Stack gap="sm">
      {availableEvents.map((event) => (
        <Checkbox
          key={event.type}
          disabled={disabled}
          label={t(event.label)}
          description={t(event.description)}
          checked={selectedEventTypes.includes(event.type)}
          onChange={(e) => toggle(event.type, e.currentTarget.checked)}
        />
      ))}
    </Stack>
  );
}
